import cn from "classnames";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";
import { Button } from "~/atoms/button";
import { CalendarIcon, Trash } from "lucide-react";
import { Calendar } from "~/atoms/calendar";
import { formatDate } from "~/helpers/date";
import dayjs from "dayjs";
import { useState } from "react";
import { useLanguageContext } from "~/context/language";

interface Props {
  value?: Date | null;
  disabled?: boolean;
  onChange?: (date?: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  align?: "start" | "center" | "end";
}

const DatePicker = ({
  onChange,
  value,
  disabled,
  minDate,
  maxDate,
  align = "start",
}: Props) => {
  const { locale } = useLanguageContext();

  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleSelect = (date?: Date) => {
    onChange?.(date);
    if (date) setOpen(false);
  };

  const handleClear = () => {
    onChange?.(undefined);
  };

  const disabledRange =
    minDate && maxDate
      ? { before: minDate, after: maxDate }
      : minDate
        ? { before: minDate }
        : maxDate
          ? { after: maxDate }
          : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-between text-left font-normal",
              !value && "text-muted-foreground",
            )}
          >
            <div className="flex items-center gap-2">
              <CalendarIcon />
              {value ? (
                formatDate(value)
              ) : (
                <span>{t("commons.customSelect.selectDate")}</span>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
            <Trash />
          </Button>
        )}
      </div>
      <PopoverContent className="h-88 w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={handleSelect}
          autoFocus
          disabled={disabledRange}
          defaultMonth={value ?? minDate ?? undefined}
          locale={locale}
          captionLayout="dropdown"
          startMonth={
            minDate ?? dayjs().subtract(10, "year").startOf("year").toDate()
          }
          endMonth={maxDate ?? dayjs().add(10, "year").endOf("year").toDate()}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
